import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { 
  ArrowRight, 
  ArrowLeft,
  CheckCircle,
  Rocket, 
  Shield, 
  Palette, 
  Users, 
  Settings, 
  Target,
  Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/services/${slug}`);
        setService(response.data);
      } catch (error) {
        console.error('Error fetching service:', error);
        setError('Service not found');
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [slug]);

  const getServiceIcon = (iconName) => {
    const icons = { 
      rocket: Rocket, 
      shield: Shield, 
      palette: Palette, 
      users: Users, 
      settings: Settings, 
      presentation: Award 
    };
    return icons[iconName] || Target;
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (error || !service) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center">
          <h1 className="text-2xl font-bold text-[#0A0F1C] mb-4">Service Not Found</h1>
          <Link to="/services" className="text-[#1D4ED8] hover:underline">
            ← Back to Services
          </Link>
        </div>
      </Layout>
    );
  }

  const Icon = getServiceIcon(service.icon);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="section-padding bg-white" data-testid="service-detail-hero">
        <div className="container-custom">
          <Link 
            to="/services" 
            className="inline-flex items-center gap-2 text-[#475569] hover:text-[#1D4ED8] mb-8"
            data-testid="back-to-services"
          >
            <ArrowLeft size={18} />
            Back to Services
          </Link>
          
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#EFF6FF] rounded-lg flex items-center justify-center">
                  <Icon size={32} className="text-[#1D4ED8]" />
                </div>
                <div>
                  {service.framework_stages && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {service.framework_stages.map((stage, i) => (
                        <span key={i} className="badge-blue text-xs">{stage}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <h1 className="heading-1 text-3xl sm:text-4xl lg:text-5xl text-[#0A0F1C] mb-6" data-testid="service-title">
                {service.title}
              </h1>
              
              <p className="body-text text-[#475569] text-lg mb-8" data-testid="service-description">
                {service.description}
              </p>
            </div>
            
            {/* Sidebar CTA */}
            <div className="lg:col-span-1">
              <div className="bg-[#FAFAFA] p-8 border border-[#E2E8F0] sticky top-24">
                <h3 className="font-bold text-[#0A0F1C] mb-4">Ready to get started?</h3>
                <p className="text-sm text-[#475569] mb-6">
                  Book a strategy session to discuss how this service can help you achieve your goals.
                </p>
                <Link 
                  to="/schedule" 
                  className="btn-primary-orange w-full inline-flex items-center justify-center gap-2 mb-4"
                  data-testid="service-cta-book"
                >
                  FREE 20-Minute Business Jumpstart Session
                  <ArrowRight size={18} />
                </Link>
                <Link 
                  to="/contact" 
                  className="btn-outline w-full inline-flex items-center justify-center"
                  data-testid="service-cta-contact"
                >
                  Ask a Question
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-[#FAFAFA]" data-testid="service-features">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* What's Included */}
            <div>
              <h2 className="heading-2 text-2xl text-[#0A0F1C] mb-6">What's Included</h2>
              <div className="space-y-4">
                {service.features && service.features.map((feature, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-3 bg-white p-4 border border-[#E2E8F0]"
                    data-testid={`feature-${index}`}
                  >
                    <CheckCircle size={20} className="text-[#1D4ED8] mt-0.5 flex-shrink-0" />
                    <span className="text-[#0A0F1C]">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Who It's For */}
            <div>
              <h2 className="heading-2 text-2xl text-[#0A0F1C] mb-6">Who This Is For</h2>
              <div className="space-y-4">
                {service.who_its_for && service.who_its_for.map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-3 bg-white p-4 border border-[#E2E8F0]"
                    data-testid={`who-its-for-${index}`}
                  >
                    <Target size={20} className="text-[#F97316] mt-0.5 flex-shrink-0" />
                    <span className="text-[#0A0F1C]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#1D4ED8]" data-testid="service-final-cta">
        <div className="container-custom text-center">
          <h2 className="heading-2 text-3xl text-white mb-4">
            Ready to {service.title}?
          </h2>
          <p className="text-[#BFDBFE] text-lg mb-8 max-w-2xl mx-auto">
            Let's discuss your specific needs and create a customized plan for your business success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/schedule" 
              className="btn-primary-orange inline-flex items-center justify-center gap-2"
              data-testid="service-final-cta-book"
            >
              FREE 20-Minute Business Jumpstart Session
              <ArrowRight size={18} />
            </Link>
            <Link 
              to="/services" 
              className="bg-white/10 border border-white/30 text-white px-8 py-4 font-bold text-sm hover:bg-white/20 transition-colors inline-flex items-center justify-center"
              data-testid="service-final-cta-services"
            >
              Explore Other Services
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
